import React from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { UserProfile } from '../../models/UserProfile';

interface ProfilePageProps {
  userProfile: UserProfile;
  onSaveProfile: (details: { firstName: string; lastName: string }) => void;
}

const NAME_MAX_LENGTH = 40;

/**
 * "Profile" — who the practitioner is.
 *
 * Nothing here reaches a report. Prepared By and the Custom Logo passed through
 * this page on their way out of Settings, but both are printed on the reports
 * the practitioner produces, so they went on to Report Settings with the rest
 * of the report's appearance. What is left is the two things that are genuinely
 * account-level: the name shown in the Account menu, and the sign-in email.
 */
export const ProfilePage: React.FC<ProfilePageProps> = ({
  userProfile,
  onSaveProfile,
}) => {
  // Both stay LOCAL until Save, rather than pushing each keystroke into the
  // shared profile the way Report Settings' own fields do: the Account menu
  // reads that profile, so a half-typed name would appear up there and then
  // vanish on the next reload.
  const [firstName, setFirstName] = React.useState(userProfile.firstName || '');
  const [lastName, setLastName] = React.useState(userProfile.lastName || '');

  const boundedSetter = (
    setter: (value: string) => void,
    maxLength: number,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > maxLength) {
      return;
    }
    setter(val);
  };

  const handleFirstNameChange = boundedSetter(setFirstName, NAME_MAX_LENGTH);
  const handleLastNameChange = boundedSetter(setLastName, NAME_MAX_LENGTH);

  const handleSave = () =>
    onSaveProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });

  return (
    <div>
      {/* They were on this page until now, and before that in Settings. A
          pointer costs one line and saves a hunt through three screens. */}
      <p className="text-muted">
        Looking for <strong>Prepared By</strong> or your{' '}
        <strong>Custom Logo</strong>? They are in{' '}
        <Link to="/settings">Report Settings</Link>, under the Test Results
        menu, with the rest of what appears on your reports.
      </p>

      <h5>Practitioner Details</h5>
      <p className="text-muted">
        Your name, as it appears in the Account menu.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formFirstName">
          <Form.Label column sm={3}>
            First Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. MaryAnn"
              value={firstName}
              onChange={handleFirstNameChange}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formLastName">
          <Form.Label column sm={3}>
            Last Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. Marks"
              value={lastName}
              onChange={handleLastNameChange}
            />
            <Form.Text className="text-muted">
              This is the name shown in the Account menu at the top of the
              screen. It does not appear on reports. To put a name on your
              reports, use <strong>Prepared By</strong> in Report Settings.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={handleSave}>
              Save Practitioner Details
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Sign-in Email</h5>
      <p className="text-muted">
        The address you sign in with, and where account and subscription email
        is sent.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formEmail">
          <Form.Label column sm={3}>
            Email
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '340px' }}
              value={userProfile.email || ''}
              readOnly
              disabled
            />
            <Form.Text className="text-muted">
              This address cannot be changed here. If you need it updated, email{' '}
              <a href="mailto:support@labsmarts.com">support@labsmarts.com</a>{' '}
              and we will take care of it. Your clients, tests and report
              settings all stay with your account when it changes.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>
    </div>
  );
};
